import React, { useState, useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import apiService from '../../services/apiService';

const City = () => {
  const { stateId } = useParams();
  const { hasPermission } = useAuth();
  const [cities, setCities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [metaFilter, setMetaFilter] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const mediaUrl = process.env.REACT_APP_MEDIA_URL || '';

  useEffect(() => {
    fetchCities();
  }, [currentPage, searchQuery, metaFilter, stateId]);

  const fetchCities = async () => {
    setLoading(true);
    try {
      const response = await apiService.post('cityList', {
        stateid: stateId,
        search: searchQuery,
        pending: metaFilter,
        start: (currentPage - 1) * 10,
        length: 10
      });
      
      if (response.data) {
        setCities(response.data);
        setTotalPages(Math.ceil(response.recordsFiltered / 10));
      }
    } catch (error) {
      console.error('Error fetching cities:', error);
    } finally {
      setLoading(false);
    }
  };

  const toggleCityStatus = async (id) => {
    try {
      await apiService.get(`stateStatus/${id}`);
      setCities(prevCities => 
        prevCities.map(city => 
          city._id === id ? { ...city, status: city.status === 1 ? 0 : 1 } : city
        )
      );
    } catch (error) {
      console.error('Error updating city status:', error);
    }
  };

  const renderPagination = () => {
    const pages = [];
    const maxVisible = 5;
    let startPage = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let endPage = Math.min(totalPages, startPage + maxVisible - 1);

    if (endPage - startPage < maxVisible - 1) {
      startPage = Math.max(1, endPage - maxVisible + 1);
    }

    if (startPage > 1) {
      pages.push(
        <button key="first" onClick={() => setCurrentPage(1)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">«</button>
      );
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(
        <button
          key={i}
          onClick={() => setCurrentPage(i)}
          className={`px-3 py-1 border rounded-lg ${currentPage === i ? 'bg-[#009ef7] text-white border-[#009ef7]' : 'border-gray-300 hover:bg-gray-50'}`}
        >
          {i}
        </button>
      );
    }

    if (endPage < totalPages) {
      pages.push(
        <button key="last" onClick={() => setCurrentPage(totalPages)} className="px-3 py-1 border border-gray-300 rounded-lg hover:bg-gray-50">»</button>
      );
    }

    return pages;
  };

  return (
    <div className="p-0">
      <div className="mb-6">
        <nav className="flex flex-wrap items-center text-xs sm:text-sm">
          <Link to="/dashboard" className="text-gray-500 hover:text-gray-700">Home</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">Locations</span>
          <span className="mx-2 text-gray-400">/</span>
          <Link to="/country" className="text-gray-500 hover:text-gray-700">Country</Link>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-500">State</span>
          <span className="mx-2 text-gray-400">/</span>
          <span className="text-gray-900 font-medium">Cities</span>
        </nav>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="p-4 flex flex-col sm:flex-row gap-3">
          <div className="flex-1 min-w-[200px]">
            <div className="relative">
              <i className="bi bi-search absolute left-3 top-1/2 -translate-y-1/2 text-gray-400"></i>
              <input
                type="text"
                placeholder="Search query here.."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
              />
            </div>
          </div>
          <select
            value={metaFilter}
            onChange={(e) => setMetaFilter(e.target.value)}
            className="w-full sm:w-auto px-2 sm:px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009ef7] focus:border-transparent"
          >
            <option value="All">Show All</option>
            <option value="1">Content</option>
            <option value="0">No Meta Data</option>
          </select>
        </div>
      </div>

      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="overflow-x-auto">
          <table className="w-full text-xs sm:text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="px-2 sm:px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">#</th>
                <th className="px-2 sm:px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Image</th>
                <th className="px-2 sm:px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Name</th>
                <th className="px-2 sm:px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">Live</th>
                <th className="px-2 sm:px-2 sm:px-4 py-3 text-left text-xs font-semibold text-gray-600 uppercase">View</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="5" className="px-2 sm:px-4 py-8 text-center text-gray-500">
                    <i className="bi bi-arrow-repeat animate-spin text-2xl"></i>
                    <p className="mt-2">Loading...</p>
                  </td>
                </tr>
              ) : cities.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-2 sm:px-4 py-8 text-center text-gray-500">No cities found</td>
                </tr>
              ) : (
                cities.map((city, index) => (
                  <tr key={city._id} className="hover:bg-gray-50">
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-900">{(currentPage - 1) * 10 + index + 1}</td>
                    <td className="px-2 sm:px-4 py-3">
                      <img
                        src={city.image ? `${mediaUrl}${city.image}` : '/assets/media/svg/files/blank-image.svg'}
                        alt={city.name}
                        className="w-20 sm:w-32 h-16 sm:h-20 object-cover rounded"
                      />
                    </td>
                    <td className="px-2 sm:px-4 py-3 text-xs sm:text-sm text-gray-900 font-medium">{city.name}</td>
                    <td className="px-2 sm:px-4 py-3">
                      <label className="relative inline-flex items-center cursor-pointer">
                        <input
                          type="checkbox"
                          checked={city.status === 1}
                          onChange={() => toggleCityStatus(city._id)}
                          className="sr-only peer"
                        />
                        <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-[#009ef7]/30 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-[#009ef7]"></div>
                      </label>
                    </td>
                    <td className="px-2 sm:px-4 py-3">
                      <a
                        href={`https://travellinglark.com/trip/${city.slug}/${city.id}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center justify-center w-8 h-8 bg-[#009ef7]/10 hover:bg-[#009ef7]/20 rounded-lg transition-colors"
                      >
                        <i className="bi bi-eye text-[#009ef7]"></i>
                      </a>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {!loading && cities.length > 0 && totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-4 py-3 border-t border-gray-200">
            <div className="text-xs sm:text-sm text-gray-700">
              Showing {(currentPage - 1) * 10 + 1} to {Math.min(currentPage * 10, (currentPage - 1) * 10 + cities.length)} entries
            </div>
            <div className="flex gap-1 flex-wrap justify-center">
              {renderPagination()}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default City;
